import * as React from "react";

import { useHistory, useLocation } from "react-router";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { get } from "lodash";
import qs from "qs";

import { UsersService } from "@/services/AccountsService";
import { URLS, userDefaultRedirectUrl, useSocialCoachSession } from "@/utils";
import { Button, Card, Field, Input, InputGroup } from "@/components/ui";
import { Png } from "@/assets";
import { Eye, EyeOff } from "lucide-react";

const loginSchema = z.object({
  username: z.email().trim().min(1, "Email is Required"),
  password: z.string().min(1, "Password is Required"),
});

type LoginValues = z.infer<typeof loginSchema>;

interface OwnProps {}

type Props = OwnProps;

const IntegratedLoginPage: React.FC<Props> = ({ group }) => {
  const history = useHistory();
  const location = useLocation<{ from?: any }>();
  const { refreshSession } = useSocialCoachSession();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    const queryParams = qs.parse(location.search.slice(1));
    if (queryParams["expired"]) {
      setSessionExpired(true);
    }
  }, [location.search]);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: get(location, "state.username", ""),
      password: "",
    },
  });

  const toForgot = (): void => {
    history.push({
      pathname: URLS.forgotPassword,
      state: location.state,
    });
  };

  const toSSO = (): void => {
    history.push({
      pathname: URLS.signInSSO,
      state: location.state,
    });
  };

  const onSubmit = async (data: LoginValues) => {
    setIsSubmitting(true);
    setServerError(null);
    try {
      const loginResponse = await UsersService.login(data.username, data.password);

      if (loginResponse?.account) {
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get("pkce") === "true") {
          searchParams.delete("pkce");
          window.location.href = `${URLS.pkceAuth}?${searchParams.toString()}`;
          return;
        }

        const newSession = await refreshSession();
        const locationState = location.state;
        if (locationState?.from) {
          history.push(locationState.from);
          return;
        }
        window.location.href = userDefaultRedirectUrl(newSession);
      }
    } catch (e) {
      if (e === "BAD_CREDENTIALS") {
        form.setError("username", { message: "Invalid email address or password" });
      } else if (e === "NO_ACTIVE_PLAYER") {
        form.setError("username", {
          message:
            "Your account is no longer active. In order to activate your account, please contact us at support@socialcoach.io",
        });
      } else if (e === "LOCKED_USER") {
        form.setError("username", {
          message: "Account not activated. Please check your email or contact us at support@socialcoach.io for help.",
        });
      } else if (e === "NO_ACTIVE_COACH") {
        form.setError("username", { message: "Your account is not active" });
      } else {
        setServerError(String(e));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-200px)] p-4 sm:p-6">
      <Card className="w-full max-w-[550px] p-6 sm:p-10 shadow-lg border-slate-200">
        <div className="flex flex-col mb-6 gap-2">
          <div className="flex justify-center">
            <img src={Png.Logo} alt="SocialCoach" className="w-48 sm:w-60" />
          </div>
          <p className="text-slate-600 text-center font-semibold text-lg">
            {sessionExpired ? "Your session has expired. Please log in again." : "Welcome back!"}
          </p>
        </div>

        {serverError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md">{serverError}</div>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Controller
            name="username"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Email Address" error={fieldState.error?.message}>
                <Input {...field} type="email" placeholder="email@example.com" disabled={isSubmitting} />
              </Field>
            )}
          />

          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Password" error={fieldState.error?.message}>
                <InputGroup
                  append={
                    <InputGroup.Button
                      size="icon-xs"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </InputGroup.Button>
                  }
                  control={controlProps => (
                    <Input
                      {...field}
                      {...controlProps}
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      disabled={isSubmitting}
                    />
                  )}
                />
              </Field>
            )}
          />

          <Button type="submit" size="lg" className="w-full h-11 sm:h-12 text-base mt-2" loading={isSubmitting}>
            Next
          </Button>

          <div className="flex justify-between items-center py-4 gap-2">
            <button
              type="button"
              onClick={toForgot}
              className="text-sm text-slate-500 hover:text-primary tracking-wide cursor-pointer font-sans"
            >
              Forgot your password?
            </button>
            <button
              type="button"
              onClick={toSSO}
              className="text-sm text-primary hover:text-slate-600 tracking-wide cursor-pointer font-sans"
            >
              Sign in with SSO
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export const LoginPage = IntegratedLoginPage;
